// frontend/modules/customer-category/views/FieldConfigurationView.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
    Card,
    Typography,
    Tabs,
    Table,
    Select,
    Switch,
    Input,
    Form,
    Button,
    message,
    Space
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { useFieldConfiguration } from '../hooks/useFieldConfiguration';
import { useCustomerCategories } from '../hooks/useCustomerCategories';
import {CustomerCategoryFieldDTO} from "@/shared/api/model";
import { FieldMetadata } from '../types';

const { Title } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

interface FieldRow {
    key: string;
    name: string;
    field: string;
    metadata: FieldMetadata;
}

const FieldConfigurationView: React.FC = () => {
    const { dark } = useTheme();
    const [form] = Form.useForm();
    const [selectedCategory, setSelectedCategory] = useState<number | null>(null);

    const {
        entityFields,
        categoryFields,
        isLoading,
        fetchCategoryFields} = useFieldConfiguration();

    const {
        categories,
        fetchCategories
    } = useCustomerCategories();

    // Use refs to store the latest function references to avoid dependency issues
    const fetchCategoriesRef = useRef(fetchCategories);
    const fetchCategoryFieldsRef = useRef(fetchCategoryFields);
    fetchCategoriesRef.current = fetchCategories;
    fetchCategoryFieldsRef.current = fetchCategoryFields;

    useEffect(() => {
        fetchCategoriesRef.current();
    }, []); // Only run once on mount, use refs for function calls

    useEffect(() => {
        if (selectedCategory) {
            fetchCategoryFieldsRef.current(selectedCategory);
        }
    }, [selectedCategory]); // Only depend on selectedCategory, use ref for function call

    const handleCategoryChange = (value: number) => {
        setSelectedCategory(value);
    };

    const handleSaveConfiguration = async (values: any) => {
        if (!selectedCategory) {
            message.error('Please select a category first');
            return;
        }

        try {
            // Transform form values to field configuration
            const customerFields: CustomerCategoryFieldDTO[] = [];
            const custInfoFields: CustomerCategoryFieldDTO[] = [];

            // Process customer fields
            if (entityFields?.customerFields) {
                Object.entries(entityFields.customerFields).forEach(([fieldName, metadata]: [string, FieldMetadata]) => {
                    if (values[`customer_${fieldName}_visible`]) {
                        customerFields.push({
                            fieldName,
                            entityType: 'Customer',
                            isMandatory: values[`customer_${fieldName}_mandatory`] || false,
                            customLabel: values[`customer_${fieldName}_label`] || metadata.label,
                            isVisible: true,
                            displayOrder: customerFields.length
                        });
                    }
                });
            }

            // Process custInfo fields
            if (entityFields?.custInfoFields) {
                Object.entries(entityFields.custInfoFields).forEach(([fieldName, metadata]: [string, FieldMetadata]) => {
                    if (values[`custInfo_${fieldName}_visible`]) {
                        custInfoFields.push({
                            fieldName,
                            entityType: 'CustInfo',
                            isMandatory: values[`custInfo_${fieldName}_mandatory`] || false,
                            customLabel: values[`custInfo_${fieldName}_label`] || metadata.label,
                            isVisible: true,
                            displayOrder: custInfoFields.length
                        });
                    }
                });
            }

            const allFields = [...customerFields, ...custInfoFields];
            // await saveFieldConfiguration(selectedCategory, allFields);
            message.success('Field configuration saved successfully');

        } catch (error) {
            message.error('Failed to save field configuration');
        }
    };

    // Initialize form with existing configuration
    useEffect(() => {
        if (entityFields) {
            const initialValues: any = {};

            // Process existing customer fields
            categoryFields.forEach(field => {
                const prefix = field.entityType === 'Customer' ? 'customer_' : 'custInfo_';
                initialValues[`${prefix}${field.fieldName}_visible`] = field.isVisible;
                initialValues[`${prefix}${field.fieldName}_mandatory`] = field.isMandatory;
                initialValues[`${prefix}${field.fieldName}_label`] = field.customLabel;
            });

            // Set default values for fields not in configuration
            if (entityFields.customerFields) {
                Object.keys(entityFields.customerFields).forEach(fieldName => {
                    if (initialValues[`customer_${fieldName}_visible`] === undefined) {
                        initialValues[`customer_${fieldName}_visible`] = true;
                        initialValues[`customer_${fieldName}_mandatory`] =
                            entityFields.customerFields[fieldName].defaultRequired;
                        initialValues[`customer_${fieldName}_label`] =
                            entityFields.customerFields[fieldName].label;
                    }
                });
            }

            if (entityFields.custInfoFields) {
                Object.keys(entityFields.custInfoFields).forEach(fieldName => {
                    if (initialValues[`custInfo_${fieldName}_visible`] === undefined) {
                        initialValues[`custInfo_${fieldName}_visible`] = true;
                        initialValues[`custInfo_${fieldName}_mandatory`] =
                            entityFields.custInfoFields[fieldName].defaultRequired;
                        initialValues[`custInfo_${fieldName}_label`] =
                            entityFields.custInfoFields[fieldName].label;
                    }
                });
            }

            form.setFieldsValue(initialValues);
        }
    }, [categoryFields, entityFields, form]);

    const renderFieldConfigTable = (fields: Record<string, FieldMetadata>, entityType: string) => {
        const columns = [
            {
                title: 'Field',
                dataIndex: 'field',
                key: 'field',
                width: '20%'
            },
            {
                title: 'Label',
                key: 'label',
                width: '30%',
                render: (_: any, record: FieldRow) => (
                    <Form.Item
                        name={`${entityType}_${record.name}_label`}
                        noStyle
                    >
                        <Input placeholder={record.metadata.label} />
                    </Form.Item>
                )
            },
            {
                title: 'Visible',
                key: 'visible',
                width: '15%',
                render: (_: any, record: FieldRow) => (
                    <Form.Item
                        name={`${entityType}_${record.name}_visible`}
                        valuePropName="checked"
                        noStyle
                    >
                        <Switch />
                    </Form.Item>
                )
            },
            {
                title: 'Mandatory',
                key: 'mandatory',
                width: '15%',
                render: (_: any, record: FieldRow) => (
                    <Form.Item
                        name={`${entityType}_${record.name}_mandatory`}
                        valuePropName="checked"
                        noStyle
                    >
                        <Switch />
                    </Form.Item>
                )
            },
            {
                title: 'Type',
                dataIndex: ['metadata', 'type'],
                key: 'type',
                width: '20%'
            }
        ];

        const data: FieldRow[] = Object.entries(fields).map(([fieldName, metadata]: [string, FieldMetadata]) => ({
            key: fieldName,
            name: fieldName,
            field: metadata.label,
            metadata
        }));

        return (
            <Table
                dataSource={data}
                columns={columns}
                pagination={false}
                size="middle"
                // @ts-ignore
                rowClassName={(record, index) =>
                    (index % 2 === 0 ? (dark ? 'bg-gray-800' : 'bg-gray-50') : '')
                }
            />
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <Title level={2} className={dark ? 'text-white' : ''}>
                    Field Configuration
                </Title>
            </div>

            <Card className={dark ? 'bg-gray-800' : ''} loading={isLoading}>
                <div className="mb-6">
                    <Space size="large">
                        <div>
                            <span className="mr-2">Customer Category:</span>
                            <Select
                                style={{ width: 300 }}
                                placeholder="Select a category"
                                onChange={handleCategoryChange}
                                value={selectedCategory}
                            >
                                {categories.map(category => (
                                    <Option key={category.id} value={category.id!}>
                                        {category.name}
                                    </Option>
                                ))}
                            </Select>
                        </div>

                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={() => form.submit()}
                            disabled={!selectedCategory}
                        >
                            Save Configuration
                        </Button>
                    </Space>
                </div>

                {selectedCategory && entityFields && (
                    <Form
                        form={form}
                        onFinish={handleSaveConfiguration}
                        layout="vertical"
                    >
                        <Tabs defaultActiveKey="customer">
                            <TabPane tab="Customer Information" key="customer">
                                {entityFields.customerFields &&
                                    renderFieldConfigTable(entityFields.customerFields, 'customer')}
                            </TabPane>

                            <TabPane tab="Additional Information" key="custInfo">
                                {entityFields.custInfoFields &&
                                    renderFieldConfigTable(entityFields.custInfoFields, 'custInfo')}
                            </TabPane>
                        </Tabs>
                    </Form>
                )}

                {!selectedCategory && (
                    <div className="text-center py-10">
                        <Typography.Text className={dark ? 'text-gray-400' : 'text-gray-600'}>
                            Please select a customer category to configure fields
                        </Typography.Text>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default FieldConfigurationView;